import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useNotify, useTranslate } from "react-admin";

import {
  CheckLogisticsDhlConnectionDocument,
  type CheckLogisticsDhlConnectionMutation,
  type CheckLogisticsDhlConnectionMutationVariables,
  LogisticsDhlAccountSettingsDocument,
  type LogisticsDhlAccountSettingsQuery,
  LogisticsDhlConnectionFailure,
  LogisticsDhlSetting,
  UpdateLogisticsDhlAccountSettingsDocument,
  type UpdateLogisticsDhlAccountSettingsMutation,
  type UpdateLogisticsDhlAccountSettingsMutationVariables,
} from "../../generated/graphql";
import { captureException } from "../../lib/errorHandling";
import { dhlSettingLabelKey, invalidDhlSettings, writeDhlAccountSettings } from "../../lib/logisticsDhlSettings";

type Settings = LogisticsDhlAccountSettingsQuery["logisticsDhlAccountSettings"];
type FormValues = {
  clientId: string;
  clientSecret: string;
  username: string;
  password: string;
  billingNumberPaket: string;
  billingNumberPaketInternational: string;
  billingNumberRetoure: string;
  returnReceiverIdAustria: string;
};

const formValues = (saved: Settings): FormValues => ({
  clientId: saved.clientId ?? "",
  clientSecret: "",
  username: saved.username ?? "",
  password: "",
  billingNumberPaket: saved.billingNumberPaket ?? "",
  billingNumberPaketInternational: saved.billingNumberPaketInternational ?? "",
  billingNumberRetoure: saved.billingNumberRetoure ?? "",
  returnReceiverIdAustria: saved.returnReceiverIdAustria ?? "",
});

const fields: { key: keyof FormValues; setting: LogisticsDhlSetting }[] = [
  { key: "clientId", setting: LogisticsDhlSetting.ClientId },
  { key: "clientSecret", setting: LogisticsDhlSetting.ClientSecret },
  { key: "username", setting: LogisticsDhlSetting.Username },
  { key: "password", setting: LogisticsDhlSetting.Password },
  { key: "billingNumberPaket", setting: LogisticsDhlSetting.BillingNumberPaket },
  { key: "billingNumberPaketInternational", setting: LogisticsDhlSetting.BillingNumberPaketInternational },
  { key: "billingNumberRetoure", setting: LogisticsDhlSetting.BillingNumberRetoure },
  { key: "returnReceiverIdAustria", setting: LogisticsDhlSetting.ReturnReceiverIdAt },
];

type Connection = CheckLogisticsDhlConnectionMutation["checkLogisticsDhlConnection"];
type ConnectionFeedback =
  | { kind: "success" }
  | { kind: "failure"; failure: LogisticsDhlConnectionFailure; missingSettings: LogisticsDhlSetting[] }
  | { kind: "error" };

const connectionFailureKeys: Record<LogisticsDhlConnectionFailure, string> = {
  [LogisticsDhlConnectionFailure.NotConfigured]: "dhlSettings.connectionFailures.NOT_CONFIGURED",
  [LogisticsDhlConnectionFailure.Unauthorized]: "dhlSettings.connectionFailures.UNAUTHORIZED",
  [LogisticsDhlConnectionFailure.Unreachable]: "dhlSettings.connectionFailures.UNREACHABLE",
  [LogisticsDhlConnectionFailure.RateLimited]: "dhlSettings.connectionFailures.RATE_LIMITED",
  [LogisticsDhlConnectionFailure.ProviderError]: "dhlSettings.connectionFailures.PROVIDER_ERROR",
  [LogisticsDhlConnectionFailure.InvalidResponse]: "dhlSettings.connectionFailures.INVALID_RESPONSE",
};

export const DhlAccountSettings = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const { data, loading, error } = useQuery<LogisticsDhlAccountSettingsQuery>(LogisticsDhlAccountSettingsDocument, {
    fetchPolicy: "network-only",
  });
  const [form, setForm] = useState<FormValues | null>(null);
  const [invalid, setInvalid] = useState<LogisticsDhlSetting[]>([]);
  const [requiredSecrets, setRequiredSecrets] = useState<LogisticsDhlSetting[]>([]);
  const saveInFlight = useRef(false);
  const checkInFlight = useRef(false);
  const [connection, setConnection] = useState<Connection | null>(null);
  const [connectionFeedback, setConnectionFeedback] = useState<ConnectionFeedback | null>(null);
  const credentialBaseline = useRef<string | null>(null);
  const [checkConnection, { loading: checking }] = useMutation<
    CheckLogisticsDhlConnectionMutation,
    CheckLogisticsDhlConnectionMutationVariables
  >(CheckLogisticsDhlConnectionDocument);
  const [saveSettings, { loading: saving }] = useMutation<
    UpdateLogisticsDhlAccountSettingsMutation,
    UpdateLogisticsDhlAccountSettingsMutationVariables
  >(UpdateLogisticsDhlAccountSettingsDocument, {
    update: (cache, { data: mutationData }) => {
      const saved = mutationData?.updateLogisticsDhlAccountSettings;
      if (saved) writeDhlAccountSettings(cache, saved);
    },
  });
  const settings = data?.logisticsDhlAccountSettings;

  useEffect(() => {
    if (!loading && !error && settings && form === null) setForm(formValues(settings));
  }, [loading, error, settings, form]);

  const currentCredentialBaseline = settings
    ? JSON.stringify([settings.clientId, settings.username, settings.hasClientSecret, settings.hasPassword])
    : null;
  useEffect(() => {
    if (credentialBaseline.current !== null && credentialBaseline.current !== currentCredentialBaseline) {
      setConnection(null);
      setConnectionFeedback(null);
    }
    credentialBaseline.current = currentCredentialBaseline;
  }, [currentCredentialBaseline]);

  const savedForm = settings ? formValues(settings) : null;
  const hasUnsavedChanges =
    !!form &&
    !!savedForm &&
    fields.some(({ key }) =>
      key === "clientSecret" || key === "password" ? form[key] !== "" : form[key].trim() !== savedForm[key].trim()
    );
  const formDisabled = saving || checking;

  const handleConnectionCheck = async () => {
    if (
      !settings ||
      !form ||
      loading ||
      error ||
      hasUnsavedChanges ||
      formDisabled ||
      saveInFlight.current ||
      checkInFlight.current
    )
      return;
    checkInFlight.current = true;
    setConnection(null);
    setConnectionFeedback(null);
    try {
      const result = await checkConnection();
      if (credentialBaseline.current !== currentCredentialBaseline) return;
      const checked = result.data?.checkLogisticsDhlConnection;
      if (!checked) throw new Error();
      if (checked.ok && checked.failure == null && Array.isArray(checked.austrianReturnReceivers)) {
        setConnection(checked);
        setConnectionFeedback({ kind: "success" });
      } else if (
        !checked.ok &&
        checked.failure &&
        Object.prototype.hasOwnProperty.call(connectionFailureKeys, checked.failure)
      ) {
        setConnectionFeedback({ kind: "failure", failure: checked.failure, missingSettings: checked.missingSettings });
      } else throw new Error();
    } catch {
      if (credentialBaseline.current !== currentCredentialBaseline) return;
      captureException(new Error("DHL connection check failed"), "logistics/dhl: check connection");
      setConnectionFeedback({ kind: "error" });
    } finally {
      checkInFlight.current = false;
    }
  };

  const clientChanged = !!form && form.clientId.trim() !== (settings?.clientId ?? "");
  const usernameChanged = !!form && form.username.trim() !== (settings?.username ?? "");
  const needsClientSecret = !settings?.hasClientSecret || clientChanged;
  const needsPassword = !settings?.hasPassword || usernameChanged;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form || loading || error || formDisabled || saveInFlight.current || checkInFlight.current) return;
    const missing: LogisticsDhlSetting[] = [];
    if (needsClientSecret && !form.clientSecret) missing.push(LogisticsDhlSetting.ClientSecret);
    if (needsPassword && !form.password) missing.push(LogisticsDhlSetting.Password);
    setRequiredSecrets(missing);
    setInvalid([]);
    if (missing.length) {
      notify(translate("dhlSettings.invalid"), { type: "error" });
      return;
    }
    saveInFlight.current = true;
    try {
      const result = await saveSettings({
        variables: {
          input: {
            ...form,
            clientSecret: form.clientSecret || undefined,
            password: form.password || undefined,
          },
        },
      });
      const saved = result.data?.updateLogisticsDhlAccountSettings;
      if (!saved) throw new Error();
      setForm(formValues(saved));
      notify("dhlSettings.saved", { type: "success" });
    } catch (saveError) {
      const invalidFields = invalidDhlSettings(saveError);
      setInvalid(invalidFields);
      if (!invalidFields.length)
        captureException(new Error("DHL account settings save failed"), "logistics/dhl: save account settings");
      notify(translate(invalidFields.length ? "dhlSettings.invalid" : "dhlSettings.saveError"), { type: "error" });
    } finally {
      saveInFlight.current = false;
    }
  };

  if (error) return <Alert severity="error">{translate("dhlSettings.loadError")}</Alert>;
  if (loading || !settings || !form) return <CircularProgress aria-label={translate("dhlSettings.loading")} />;

  const secretHelper = (needed: boolean, changed: boolean): string =>
    translate(
      changed ? "dhlSettings.secretRequiredAgain" : needed ? "dhlSettings.secretRequired" : "dhlSettings.secretStored"
    );

  return (
    <Card>
      <CardContent>
        <Typography component="h2" variant="h6" gutterBottom>
          {translate("dhlSettings.accountTitle")}
        </Typography>
        <Typography color="text.secondary" paragraph>
          {translate("dhlSettings.accountDescription")}
        </Typography>
        {settings.missingSettings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {translate("dhlSettings.missingSettings")}
            <Box component="ul" sx={{ mb: 0 }}>
              {settings.missingSettings.map(setting => (
                <li key={setting}>{translate(dhlSettingLabelKey(setting))}</li>
              ))}
            </Box>
          </Alert>
        )}
        <TextField
          fullWidth
          margin="normal"
          label={translate("dhlSettings.gateway")}
          value={translate(`dhlSettings.gateways.${settings.gateway}`)}
          slotProps={{ input: { readOnly: true } }}
          disabled={formDisabled}
        />
        <Box component="form" aria-label={translate("dhlSettings.accountTitle")} onSubmit={handleSubmit} noValidate>
          {fields.map(({ key, setting }) => {
            const secret = key === "clientSecret" || key === "password";
            const needed = key === "clientSecret" ? needsClientSecret : needsPassword;
            const changed = key === "clientSecret" ? clientChanged : usernameChanged;
            const receiverSelection = key === "returnReceiverIdAustria" && connection !== null;
            return (
              <TextField
                key={key}
                fullWidth
                margin="normal"
                required={!secret || needed}
                disabled={formDisabled}
                label={translate(dhlSettingLabelKey(setting))}
                select={receiverSelection}
                type={secret ? "password" : "text"}
                autoComplete={secret ? "new-password" : undefined}
                value={receiverSelection ? form[key].trim() : form[key]}
                onChange={event => {
                  if (saveInFlight.current || checkInFlight.current) return;
                  setConnectionFeedback(null);
                  if (key === "clientId" || key === "clientSecret" || key === "username" || key === "password")
                    setConnection(null);
                  const nextForm = { ...form, [key]: event.target.value };
                  setInvalid(current => current.filter(field => field !== setting));
                  setRequiredSecrets(current =>
                    current.filter(
                      field =>
                        field !== setting &&
                        (field !== LogisticsDhlSetting.ClientSecret ||
                          !settings.hasClientSecret ||
                          nextForm.clientId.trim() !== (settings.clientId ?? "")) &&
                        (field !== LogisticsDhlSetting.Password ||
                          !settings.hasPassword ||
                          nextForm.username.trim() !== (settings.username ?? ""))
                    )
                  );
                  setForm(nextForm);
                }}
                error={invalid.includes(setting) || requiredSecrets.includes(setting)}
                helperText={
                  requiredSecrets.includes(setting)
                    ? secretHelper(needed, changed)
                    : invalid.includes(setting)
                      ? translate("dhlSettings.fieldInvalid")
                      : secret
                        ? secretHelper(needed, changed)
                        : undefined
                }
              >
                {receiverSelection &&
                  connection && [
                    ...(settings.returnReceiverIdAustria &&
                    !connection.austrianReturnReceivers.some(
                      receiver => receiver.receiverId === settings.returnReceiverIdAustria
                    )
                      ? [
                          <MenuItem key="stored" value={settings.returnReceiverIdAustria} disabled>
                            {translate("dhlSettings.unlistedReceiverOption", {
                              receiverId: settings.returnReceiverIdAustria,
                            })}
                          </MenuItem>,
                        ]
                      : []),
                    ...connection.austrianReturnReceivers.map(receiver => (
                      <MenuItem key={`receiver:${receiver.receiverId}`} value={receiver.receiverId}>
                        {[receiver.receiverId, receiver.name, receiver.postalCode, receiver.city]
                          .filter(Boolean)
                          .join(" · ")}
                      </MenuItem>
                    )),
                  ]}
              </TextField>
            );
          })}
          {connection &&
            settings.returnReceiverIdAustria &&
            !connection.austrianReturnReceivers.some(
              receiver => receiver.receiverId === settings.returnReceiverIdAustria
            ) && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {translate("dhlSettings.unlistedReceiver")}
              </Alert>
            )}
          {connection && connection.austrianReturnReceivers.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {translate("dhlSettings.noReceivers")}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <Button type="submit" variant="contained" disabled={formDisabled}>
              {translate(saving ? "dhlSettings.saving" : "dhlSettings.save")}
            </Button>
            <Button
              type="button"
              sx={{ ml: 1 }}
              disabled={formDisabled || hasUnsavedChanges}
              onClick={handleConnectionCheck}
            >
              {translate(checking ? "dhlSettings.checkingConnection" : "dhlSettings.checkConnection")}
            </Button>
          </Box>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {translate(hasUnsavedChanges ? "dhlSettings.saveBeforeCheck" : "dhlSettings.checkStoredSettings")}
          </Typography>
          {connectionFeedback && (
            <Alert severity={connectionFeedback.kind === "success" ? "success" : "error"} sx={{ mt: 2 }}>
              {translate(
                connectionFeedback.kind === "success"
                  ? "dhlSettings.connectionSuccess"
                  : connectionFeedback.kind === "failure"
                    ? connectionFailureKeys[connectionFeedback.failure]
                    : "dhlSettings.connectionError"
              )}
              {connectionFeedback.kind === "failure" &&
                connectionFeedback.failure === LogisticsDhlConnectionFailure.NotConfigured &&
                connectionFeedback.missingSettings.length > 0 && (
                  <Box component="ul" sx={{ mb: 0 }}>
                    {connectionFeedback.missingSettings.map(setting => (
                      <li key={setting}>{translate(dhlSettingLabelKey(setting))}</li>
                    ))}
                  </Box>
                )}
            </Alert>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
